export interface TableColumnWidthOptions {
  /** Relative weight for numeric / narrow columns (default 1). */
  numericWeight?: number;
  /** Relative weight for text / wide columns (default 2). */
  textWeight?: number;
}

/** Shared weights for line-items table columns (HTML preview + PdfMe export). */
export const DEFAULT_LINE_ITEMS_TABLE_COLUMN_WEIGHTS: TableColumnWidthOptions = {
  numericWeight: 1.35,
  textWeight: 2
};

/**
 * Distributes column widths so percentages sum to 100.
 * Used by HTML preview (`table-layout: fixed`) and PdfMe table serialization.
 */
export function buildTableColumnWidthPercentages(
  columnKeys: readonly string[],
  isNumericColumn: (key: string) => boolean,
  options: TableColumnWidthOptions = {}
): number[] {
  if (columnKeys.length === 0) {
    return [];
  }

  const numericWeight = options.numericWeight ?? 1;
  const textWeight = options.textWeight ?? 2;
  const weights = columnKeys.map((key) => (isNumericColumn(key) ? numericWeight : textWeight));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

  if (totalWeight <= 0) {
    const equal = 100 / columnKeys.length;
    return columnKeys.map(() => equal);
  }

  const raw = weights.map((weight) => (weight / totalWeight) * 100);
  const rounded = raw.map((value) => Math.round(value * 100) / 100);
  const roundedSum = rounded.reduce((sum, value) => sum + value, 0);
  const delta = Math.round((100 - roundedSum) * 100) / 100;

  if (delta !== 0 && rounded.length > 0) {
    rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1]! + delta) * 100) / 100;
  }

  return rounded;
}
